import React from 'react';
import styled from 'styled-components';
import { Col, Row } from '../../lib/utils';
import { colors } from '../../lib/colors';
import { Body2Bold, Body3Bold, OverlineRegular } from '../../lib/font';
import dayjs from 'dayjs';
import { MemberMembershipDataType } from '../../services/useMembershipQueryService';
import { MemberShipApiType } from '../../api/memberShip';
import SvgIcon from '../SvgIcon';
import { useMembershipTheme } from './useMembershipTheme';
import { CalendarPlus, ExternalLink } from 'lucide-react';
import MembershipPauseBadge from '../MembershipPause/MembershipPauseBadge';

type Props = {
    index: number;
    membership: MemberMembershipDataType | MemberShipApiType;
    onClickDetailPage: (membership: any) => void;
    onClickDelete?: (index: number) => void;
    onClickRegisterSchedule?: (membership: any) => void;
    /** 공유받아 쓰는 수강권일 때 소유자 이름. 카드 모양은 같게 두고 배지로만 구분한다. */
    sharedOwnerName?: string;
    /** 강사별로 묶이지 않는 자리에 카드를 놓을 때 담당 강사를 함께 보여준다. */
    coachName?: string;
    /**
     * 잔여 회차 라벨. 공유받은 수강권 카드는 소유자와 함께 쓰는 '예약 가능' 회차를 보여주므로
     * 개인 수강권의 '잔여'와 같은 말로 부르면 다른 숫자가 같은 이름으로 읽힌다.
     */
    remainLabel?: string;
};

const Container = styled.div`
    margin-bottom: 24px;
    margin-top: 3px;
    margin-left: 3px;
    margin-right: 20px;
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 2px 2px 12px 0px #00000029;
    width: 316px; /* 고정 너비 설정 */
    /* 공유 카드는 '공유 · ○○님' 배지와 담당 강사 줄이 추가돼 고정 160px에서는
       하단(진행 회차/유효 기간)이 overflow:hidden에 잘린다. 최소 높이만 보장한다. */
    min-height: 160px;
    &:hover {
        transform: scale(1.02);
    }
    flex-shrink: 0; /* Prevent shrinking */
    position: relative; /* Add relative positioning here */
`;

const MembershipCard = ({
    index,
    membership,
    onClickDetailPage,
    onClickDelete = undefined,
    onClickRegisterSchedule,
    sharedOwnerName,
    coachName,
    remainLabel = '잔여',
}: Props) => {
    // 유효기간
    const validDays = dayjs(membership.expirationDate).diff(dayjs(), 'day');
    // 수강권 테마 — gradient 일원화 (Phase 2 UX 통일화)
    const { headerBackground, isGroup, sourceLabel } = useMembershipTheme(membership);
    const totalSession = Number(membership?.totalSession ?? 0);
    const rawCurrentSession = membership?.currentSession;
    const rawRemainSession = membership?.remainSession;
    const currentSession =
        typeof rawCurrentSession === 'number'
            ? rawCurrentSession
            : typeof rawRemainSession === 'number'
            ? Math.max(totalSession - rawRemainSession, 0)
            : undefined;
    const remainSession =
        typeof rawRemainSession === 'number'
            ? rawRemainSession
            : typeof currentSession === 'number'
            ? Math.max(totalSession - currentSession, 0)
            : undefined;
    const sessionValue = `${typeof currentSession === 'number' ? currentSession : '-'}/${totalSession || '-'}회`;
    const remainValue =
        typeof remainSession === 'number' ? `${remainLabel} ${remainSession}회` : `${remainLabel} -`;
    const canRegisterSchedule = !isGroup && membership?.status === 'active' && Number(remainSession ?? 0) > 0;

    return (
        <Container onClick={() => onClickDetailPage(membership)} className="flex flex-col">
            {onClickDelete && (
                <Col
                    className="flex absolute right-[12px] top-[10px] cursor-pointer"
                    onClick={() => {
                        onClickDelete && onClickDelete(index);
                    }}
                >
                    <SvgIcon name={'SvgCircleX'} size={24} fill="white" />
                </Col>
            )}
            <Col
                className="flex flex-col w-full"
                style={{
                    background: headerBackground,
                    alignItems: 'center',
                }}
            >
                <Col className="flex w-full px-[32px] py-[28px] justify-start items-start">
                    <Row className="items-center gap-[8px]" style={{ maxWidth: onClickRegisterSchedule ? 190 : 252 }}>
                        <Body2Bold
                            className="text-LAVEL_0"
                            style={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {`${membership?.name}`}
                        </Body2Bold>
                        {isGroup && (
                            <span
                                style={{
                                    fontSize: 10,
                                    fontWeight: 700,
                                    padding: '2px 8px',
                                    borderRadius: 4,
                                    backgroundColor: 'rgba(255,255,255,0.22)',
                                    color: '#FFFFFF',
                                    lineHeight: 1.4,
                                }}
                            >
                                {sourceLabel}
                            </span>
                        )}
                    </Row>
                    {sharedOwnerName && (
                        <span
                            style={{
                                marginTop: 8,
                                maxWidth: 220,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                fontSize: 11,
                                fontWeight: 700,
                                padding: '3px 8px',
                                borderRadius: 4,
                                backgroundColor: 'rgba(255,255,255,0.22)',
                                color: '#FFFFFF',
                                lineHeight: 1.4,
                            }}
                        >
                            {`공유 · ${sharedOwnerName}님`}
                        </span>
                    )}
                    {/* 수강권 일시정지 배지 — 정지 정보가 없는 수강권은 아무것도 그리지 않는다 */}
                    <MembershipPauseBadge pass={membership as any} variant="onColor" className="mt-[8px] self-start" />
                    {coachName && (
                        <span
                            style={{
                                marginTop: 6,
                                fontSize: 11,
                                fontWeight: 600,
                                color: 'rgba(255,255,255,0.86)',
                            }}
                        >
                            {`담당 강사 ${coachName}`}
                        </span>
                    )}
                    {/*
                      공유받은 카드를 누르면 소유자의 수강권 상세로 이동한다.
                      누르기 전에 어디로 가는지 밝혀 둔다 — 회원 프로필에서 남의 화면으로
                      넘어가는 건 예고 없이 일어나면 길을 잃는다.
                      평문으로 두면 라벨로 읽혀 아무도 누를 수 있는 줄 모른다. 눌리는 모양을 준다.
                    */}
                    {sharedOwnerName && (
                        <span
                            style={{
                                marginTop: 8,
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 4,
                                padding: '4px 8px',
                                borderRadius: 6,
                                border: '1px solid rgba(255,255,255,0.42)',
                                backgroundColor: 'rgba(255,255,255,0.14)',
                                fontSize: 11,
                                fontWeight: 700,
                                color: '#FFFFFF',
                                lineHeight: 1.3,
                            }}
                        >
                            소유자 프로필에서 관리
                            <ExternalLink size={11} />
                        </span>
                    )}
                </Col>
            </Col>
            {onClickRegisterSchedule && (
                <button
                    type="button"
                    disabled={!canRegisterSchedule}
                    title={
                        canRegisterSchedule
                            ? '수업 등록'
                            : membership?.status !== 'active'
                            ? '활성 수강권만 등록할 수 있습니다.'
                            : '남은 회차가 없습니다.'
                    }
                    onClick={(event) => {
                        event.stopPropagation();
                        if (!canRegisterSchedule) return;
                        onClickRegisterSchedule(membership);
                    }}
                    style={{
                        position: 'absolute',
                        top: 20,
                        right: 16,
                        height: 30,
                        padding: '0 10px',
                        borderRadius: 999,
                        border: `1px solid ${canRegisterSchedule ? 'rgba(255,255,255,0.62)' : 'rgba(255,255,255,0.32)'}`,
                        backgroundColor: canRegisterSchedule ? 'rgba(255,255,255,0.18)' : 'rgba(255,255,255,0.08)',
                        color: '#FFFFFF',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 5,
                        fontSize: 12,
                        fontWeight: 800,
                        cursor: canRegisterSchedule ? 'pointer' : 'not-allowed',
                        opacity: canRegisterSchedule ? 1 : 0.55,
                        boxShadow: canRegisterSchedule ? '0 5px 14px rgba(15, 23, 42, 0.14)' : 'none',
                        transition: 'background-color 0.16s ease, transform 0.16s ease, box-shadow 0.16s ease',
                    }}
                    onMouseEnter={(event) => {
                        if (!canRegisterSchedule) return;
                        event.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.28)';
                        event.currentTarget.style.transform = 'translateY(-1px)';
                    }}
                    onMouseLeave={(event) => {
                        event.currentTarget.style.backgroundColor = canRegisterSchedule
                            ? 'rgba(255,255,255,0.18)'
                            : 'rgba(255,255,255,0.08)';
                        event.currentTarget.style.transform = 'translateY(0)';
                    }}
                >
                    <CalendarPlus size={14} />
                    수업 등록
                </button>
            )}
            <Row style={{ alignItems: 'center', justifyContent: 'center', backgroundColor: colors.WHITE_50 }}>
                <div
                    style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr 0.8fr',
                        alignItems: 'center',
                        columnGap: 16,
                        width: '100%',
                        minHeight: 76,
                        padding: '10px 24px 12px',
                    }}
                >
                    <Col style={{ gap: 2, minWidth: 0 }}>
                        <OverlineRegular style={{ color: colors.LAVEL_3 }}>{'진행 회차'}</OverlineRegular>
                        <Body3Bold style={{ color: colors.LAVEL_4 }}>{sessionValue}</Body3Bold>
                        <span
                            style={{
                                fontSize: 11,
                                lineHeight: '14px',
                                fontWeight: 700,
                                color: typeof remainSession === 'number' && remainSession <= 2 ? '#EF4444' : '#2563EB',
                            }}
                        >
                            {remainValue}
                        </span>
                    </Col>
                    <Col style={{ gap: 4, minWidth: 0 }}>
                        <OverlineRegular style={{ color: colors.LAVEL_3 }}>{'유효 기간'}</OverlineRegular>
                        <Body3Bold style={{ color: colors.LAVEL_4 }}>{`${validDays}일`}</Body3Bold>
                    </Col>
                    <Col style={{ gap: 4, minWidth: 0, alignItems: 'flex-end' }}>
                        <OverlineRegular style={{ color: colors.LAVEL_3 }}>{'시간'}</OverlineRegular>
                        <Body3Bold style={{ color: colors.LAVEL_4 }}>{`${membership?.period}분`}</Body3Bold>
                    </Col>
                </div>
            </Row>
        </Container>
    );
};

export default MembershipCard;
